<template>
	<div class="resource-planning-user-select-dropdown">
		<!-- Loading data -->
		<div
			v-if="selectedEntityType === TYPES.User && loading"
			class="resource-planning-user-select-dropdown__trigger"
		>
			<SuiSkeleton is-circle width="3.5rem" />
			<SuiIcon name="icon-arrow-down-16" />
		</div>
		<!-- Placeholder avatar for new role booking -->
		<div
			v-else-if="
				!isUserSelectionEnabled &&
				selectedEntityType === TYPES.RoleProject
			"
			v-tooltip="selectedEntityName"
			class="resource-planning-user-select-dropdown__trigger"
		>
			<SuiAvatar
				:aria-label="selectedEntityName"
				size="x-small"
				:color="PLACEHOLDER_BG_COLOR"
			>
				<SuiIcon name="icon-person-16" />
			</SuiAvatar>
			<SuiIcon name="icon-arrow-down-16" />
		</div>
		<!-- Enabled user selection for a role booking -->
		<button
			v-else-if="
				isUserSelectionEnabled &&
				selectedEntityType === TYPES.RoleProject
			"
			v-tooltip="$t('user_assign_select_add_user')"
			type="button"
			class="resource-planning-user-select-dropdown__trigger resource-planning-user-select-dropdown__trigger--interactive"
			:aria-label="$t('user_assign_select_add_user')"
			@click="toggleIsUserSelectOpen()"
		>
			<SuiAvatar size="x-small" :color="PLACEHOLDER_BG_COLOR">
				<SuiIcon name="icon-addperson-16" />
			</SuiAvatar>
			<SuiIcon name="icon-arrow-down-16" />
		</button>
		<!-- Placeholder avatar for new user booking -->
		<div
			v-else-if="selectedUser && !isUserSelectionEnabled"
			v-tooltip="selectedUser.name"
			class="resource-planning-user-select-dropdown__trigger"
		>
			<SuiAvatar
				:url="selectedUser.profilePicture?.mini"
				:initials="selectedUser.initials || ''"
				:color="selectedUser.hslColor || ''"
				:aria-label="selectedUser.name"
				size="x-small"
				show-border
			/>
			<SuiIcon name="icon-arrow-down-16" />
		</div>
		<!-- Avatar for new user booking -->
		<button
			v-else-if="selectedUser"
			v-tooltip="$t('resource-booking__move-booking')"
			type="button"
			class="resource-planning-user-select-dropdown__trigger resource-planning-user-select-dropdown__trigger--interactive"
			:aria-label="$t('resource-booking__move-booking')"
			@click="toggleIsUserSelectOpen()"
		>
			<SuiAvatar
				:url="selectedUser.profilePicture?.mini"
				:initials="selectedUser.initials || ''"
				:color="selectedUser.hslColor || ''"
				size="x-small"
				show-border
			/>
			<SuiIcon name="icon-arrow-down-16" />
		</button>
		<VDropdown
			:shown="isUserSelectOpen"
			placement="bottom-start"
			auto-hide
			:triggers="[]"
			@auto-hide="onUserSelectClose"
		>
			<template #popper>
				<ResourcePlanningUserSelect
					:title="userSelectTitle"
					:exclude-user-id="
						selectedEntityType === TYPES.RoleProject
							? null
							: selectedUser?.id
					"
					@user-selected="$emit('user-selected', $event)"
					@close="onUserSelectClose"
				/>
			</template>
		</VDropdown>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { SuiIcon, SuiAvatar, SuiSkeleton } from '@developers/scoro-ui';
import { USER_LIST_USERS_INPUT } from './constants';
import { PLACEHOLDER_BG_COLOR } from '../../constants';
import { useToggle } from '@vueuse/core';
import ResourcePlanningUserSelect from './ResourcePlanningUserSelect.vue';
import {
	useResourcePlanningUserSelectQuery,
	type User,
	type Role,
	type RoleProject,
	TYPES,
} from '@/generated/graphql';
import { useTranslation } from '@/composables';

export type ResourcePlanningUserSelectDropdownProps = {
	isUserSelectionEnabled?: boolean;
	selectedEntityId?: User['id'] | RoleProject['id'];
	selectedEntityType?: typeof TYPES.User | typeof TYPES.RoleProject;
	selectedEntityName?: Role['name'];
};

const props = defineProps<ResourcePlanningUserSelectDropdownProps>();
const emit = defineEmits<{
	(event: 'user-selected', id: User['id']): void;
	(event: 'close'): void;
}>();

const { t } = useTranslation();
const { selectedEntityId, selectedEntityType } = toRefs(props);
const isOpen = ref(false);
const [isUserSelectOpen, toggleIsUserSelectOpen] = useToggle(isOpen.value);
const selectedUserId = computed(() =>
	selectedEntityType.value === TYPES.User ? selectedEntityId.value : null
);

const onUserSelectClose = () => {
	emit('close');

	isUserSelectOpen.value = false;
};

const { result, loading } = useResourcePlanningUserSelectQuery({
	usersListInput: USER_LIST_USERS_INPUT,
});

const selectedUser = computed(() =>
	result.value?.UsersList.find((user) => user.id === selectedUserId.value)
);

const userSelectTitle = computed(() =>
	props.selectedEntityType === TYPES.User
		? t('resource-booking__move-booking')
		: t('resource-booking__assign-member')
);
</script>

<style lang="scss" scoped>
.resource-planning-user-select-dropdown {
	&__trigger {
		display: inline-flex;
		vertical-align: top;
		align-items: center;
		padding: 0.25rem 0.5rem 0.25rem 0.25rem;
		background: $neutral-10;
		border-radius: 12.5rem;

		&--interactive {
			cursor: pointer;

			&:hover {
				background: $neutral-20;
			}
		}
	}
}
</style>
